import * as z from 'zod';

export const CreateDraftPostRequest = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z.enum(['UNKNOWN', 'APPROVED', 'REJECTED']).optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Draft post to create.'),
  options: z
    .object({
      publish: z
        .boolean()
        .describe(
          'Whether the draft post should be published on creation.\n\nDefault: `false`'
        )
        .optional(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for creating a draft post.')
    .optional(),
});
export const CreateDraftPostResponse = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .describe('Status of the draft post.')
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Created draft post info.')
    .optional(),
});
export const BulkCreateDraftPostsRequest = z.object({
  draftPosts: z
    .array(
      z.object({
        _id: z.string().describe('Draft post ID.').max(38).optional(),
        title: z.string().describe('Draft post title.').max(200),
        excerpt: z
          .string()
          .describe(
            "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
          )
          .max(500)
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe('Whether the draft post is marked as featured.')
          .optional()
          .nullable(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Draft post owner's member ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the draft post is enabled.')
          .optional()
          .nullable(),
        minutesToRead: z
          .number()
          .int()
          .describe(
            'Estimated reading time of the draft post (calculated automatically).'
          )
          .optional(),
        heroImage: z
          .string()
          .describe('Image placed at the top of the blog page.')
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PUBLISHED',
            'UNPUBLISHED',
            'SCHEDULED',
            'DELETED',
            'IN_REVIEW',
          ])
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z.enum(['UNKNOWN', 'APPROVED', 'REJECTED']).optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
          )
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there are changes made to the draft post that have not yet been published.'
          )
          .optional(),
        editedDate: z
          .date()
          .describe('Date the draft post was last edited.')
          .optional()
          .nullable(),
        scheduledPublishDate: z
          .date()
          .describe('Date the draft post is scheduled to be published.')
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        url: z
          .string()
          .describe(
            'Draft post URL preview. What the URL will look like once the post is published.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the draft post was first created.')
          .optional()
          .nullable(),
        seoSlug: z
          .string()
          .describe('SEO slug.')
          .max(100)
          .optional()
          .nullable(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        previewTextParagraph: z
          .number()
          .int()
          .describe(
            'Number of paragraphs to display in a paid content preview for non-paying users.'
          )
          .min(0)
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(20),
  options: z
    .object({
      publish: z
        .boolean()
        .describe('Whether the draft post should be published after creation.')
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the full created draft post entities in the response.'
        )
        .optional(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for creating multiple draft posts.')
    .optional(),
});
export const BulkCreateDraftPostsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk actions metadata for draft post.')
          .optional(),
        item: z
          .object({
            _id: z.string().describe('Draft post ID.').max(38).optional(),
            title: z.string().describe('Draft post title.').max(200).optional(),
            excerpt: z
              .string()
              .describe(
                "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
              )
              .max(500)
              .optional()
              .nullable(),
            featured: z
              .boolean()
              .describe('Whether the draft post is marked as featured.')
              .optional()
              .nullable(),
            categoryIds: z.array(z.string()).max(10).optional(),
            memberId: z
              .string()
              .describe("Draft post owner's member ID.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            hashtags: z.array(z.string()).max(100).optional(),
            commentingEnabled: z
              .boolean()
              .describe('Whether commenting on the draft post is enabled.')
              .optional()
              .nullable(),
            minutesToRead: z
              .number()
              .int()
              .describe(
                'Estimated reading time of the draft post (calculated automatically).'
              )
              .optional(),
            heroImage: z
              .string()
              .describe('Image placed at the top of the blog page.')
              .optional(),
            tagIds: z.array(z.string()).max(30).optional(),
            relatedPostIds: z.array(z.string()).max(3).optional(),
            pricingPlanIds: z.array(z.string()).max(100).optional(),
            translationId: z
              .string()
              .describe(
                "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            language: z
              .string()
              .describe(
                'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
              )
              .optional()
              .nullable(),
            richContent: z
              .any()
              .describe(
                'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
              )
              .optional(),
            status: z
              .enum([
                'UNKNOWN',
                'PUBLISHED',
                'UNPUBLISHED',
                'SCHEDULED',
                'DELETED',
                'IN_REVIEW',
              ])
              .describe('Status of the draft post.')
              .optional(),
            moderationDetails: z
              .object({
                submittedBy: z
                  .string()
                  .describe(
                    'Member ID of the person submitting the draft post for review.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                submittedDate: z
                  .date()
                  .describe('Date the post was submitted for review.')
                  .optional()
                  .nullable(),
                status: z
                  .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
                  .describe(
                    'Status indicating whether the submission was approved or rejected by the moderator.'
                  )
                  .optional(),
                moderatedBy: z
                  .string()
                  .describe(
                    'Member ID of the person who approved or rejected the post.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                moderationDate: z
                  .date()
                  .describe('Date the post was approved or rejected.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
              )
              .optional(),
            hasUnpublishedChanges: z
              .boolean()
              .describe(
                'Indicates if there are changes made to the draft post that have not yet been published.'
              )
              .optional(),
            editedDate: z
              .date()
              .describe('Date the draft post was last edited.')
              .optional()
              .nullable(),
            scheduledPublishDate: z
              .date()
              .describe('Date the draft post is scheduled to be published.')
              .optional()
              .nullable(),
            firstPublishedDate: z
              .date()
              .describe('Date the post was first published.')
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('SEO data.')
              .optional(),
            url: z
              .string()
              .describe(
                'Draft post URL preview. What the URL will look like once the post is published.'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date the draft post was first created.')
              .optional()
              .nullable(),
            seoSlug: z
              .string()
              .describe('SEO slug.')
              .max(100)
              .optional()
              .nullable(),
            media: z
              .intersection(
                z.object({
                  displayed: z
                    .boolean()
                    .describe('Whether cover media is displayed.')
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Media alternative text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z.never().optional(),
                  }),
                  z.object({
                    embedMedia: z.never().optional(),
                    wixMedia: z
                      .object({
                        image: z.string().describe('Image details.').optional(),
                        videoV2: z
                          .string()
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Wix Media details.'),
                  }),
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z
                      .object({
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Thumbnail details.')
                          .optional(),
                        video: z
                          .object({
                            url: z
                              .string()
                              .describe('Video url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Video width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Video height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Embed media details.'),
                  }),
                ])
              )
              .describe('Post cover media.')
              .optional(),
            previewTextParagraph: z
              .number()
              .int()
              .describe(
                'Number of paragraphs to display in a paid content preview for non-paying users.'
              )
              .min(0)
              .optional()
              .nullable(),
          })
          .describe('Optional full draft post.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateDraftPostsRequest = z.object({
  options: z
    .object({
      draftPosts: z
        .array(
          z.object({
            draftPost: z
              .object({
                _id: z.string().describe('Draft post ID.').max(38),
                title: z
                  .string()
                  .describe('Draft post title.')
                  .max(200)
                  .optional(),
                excerpt: z
                  .string()
                  .describe(
                    "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                featured: z
                  .boolean()
                  .describe('Whether the draft post is marked as featured.')
                  .optional()
                  .nullable(),
                categoryIds: z.array(z.string()).max(10).optional(),
                memberId: z
                  .string()
                  .describe("Draft post owner's member ID.")
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                hashtags: z.array(z.string()).max(100).optional(),
                commentingEnabled: z
                  .boolean()
                  .describe('Whether commenting on the draft post is enabled.')
                  .optional()
                  .nullable(),
                minutesToRead: z
                  .number()
                  .int()
                  .describe(
                    'Estimated reading time of the draft post (calculated automatically).'
                  )
                  .optional(),
                heroImage: z
                  .string()
                  .describe('Image placed at the top of the blog page.')
                  .optional(),
                tagIds: z.array(z.string()).max(30).optional(),
                relatedPostIds: z.array(z.string()).max(3).optional(),
                pricingPlanIds: z.array(z.string()).max(100).optional(),
                translationId: z
                  .string()
                  .describe(
                    "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                language: z
                  .string()
                  .describe(
                    'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
                  )
                  .optional()
                  .nullable(),
                richContent: z
                  .any()
                  .describe(
                    'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                status: z
                  .enum([
                    'UNKNOWN',
                    'PUBLISHED',
                    'UNPUBLISHED',
                    'SCHEDULED',
                    'DELETED',
                    'IN_REVIEW',
                  ])
                  .optional(),
                moderationDetails: z
                  .object({
                    submittedBy: z
                      .string()
                      .describe(
                        'Member ID of the person submitting the draft post for review.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    submittedDate: z
                      .date()
                      .describe('Date the post was submitted for review.')
                      .optional()
                      .nullable(),
                    status: z
                      .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
                      .optional(),
                    moderatedBy: z
                      .string()
                      .describe(
                        'Member ID of the person who approved or rejected the post.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    moderationDate: z
                      .date()
                      .describe('Date the post was approved or rejected.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
                  )
                  .optional(),
                hasUnpublishedChanges: z
                  .boolean()
                  .describe(
                    'Indicates if there are changes made to the draft post that have not yet been published.'
                  )
                  .optional(),
                editedDate: z
                  .date()
                  .describe('Date the draft post was last edited.')
                  .optional()
                  .nullable(),
                scheduledPublishDate: z
                  .date()
                  .describe('Date the draft post is scheduled to be published.')
                  .optional()
                  .nullable(),
                firstPublishedDate: z
                  .date()
                  .describe('Date the post was first published.')
                  .optional()
                  .nullable(),
                seoData: z
                  .object({
                    tags: z
                      .array(
                        z.object({
                          type: z
                            .string()
                            .describe(
                              'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                            )
                            .optional(),
                          props: z
                            .record(z.string(), z.any())
                            .describe(
                              'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                            )
                            .optional()
                            .nullable(),
                          meta: z
                            .record(z.string(), z.any())
                            .describe(
                              'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                            )
                            .optional()
                            .nullable(),
                          children: z
                            .string()
                            .describe(
                              'SEO tag inner content. For example, `<title> inner content </title>`.'
                            )
                            .optional(),
                          custom: z
                            .boolean()
                            .describe(
                              'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                            )
                            .optional(),
                          disabled: z
                            .boolean()
                            .describe(
                              "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    settings: z
                      .object({
                        preventAutoRedirect: z
                          .boolean()
                          .describe(
                            'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                          )
                          .optional(),
                        keywords: z
                          .array(
                            z.object({
                              term: z
                                .string()
                                .describe('Keyword value.')
                                .optional(),
                              isMain: z
                                .boolean()
                                .describe(
                                  'Whether the keyword is the main focus keyword.'
                                )
                                .optional(),
                              origin: z
                                .string()
                                .describe(
                                  'The source that added the keyword terms to the SEO settings.'
                                )
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(5)
                          .optional(),
                      })
                      .describe('SEO general settings.')
                      .optional(),
                  })
                  .describe('SEO data.')
                  .optional(),
                url: z
                  .string()
                  .describe(
                    'Draft post URL preview. What the URL will look like once the post is published.'
                  )
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date the draft post was first created.')
                  .optional()
                  .nullable(),
                seoSlug: z
                  .string()
                  .describe('SEO slug.')
                  .max(100)
                  .optional()
                  .nullable(),
                media: z
                  .intersection(
                    z.object({
                      displayed: z
                        .boolean()
                        .describe('Whether cover media is displayed.')
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                        )
                        .optional(),
                      altText: z
                        .string()
                        .describe('Media alternative text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        wixMedia: z.never().optional(),
                        embedMedia: z.never().optional(),
                      }),
                      z.object({
                        embedMedia: z.never().optional(),
                        wixMedia: z
                          .object({
                            image: z
                              .string()
                              .describe('Image details.')
                              .optional(),
                            videoV2: z
                              .string()
                              .describe('Video details.')
                              .optional(),
                          })
                          .describe('Wix Media details.'),
                      }),
                      z.object({
                        wixMedia: z.never().optional(),
                        embedMedia: z
                          .object({
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .max(2000)
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .min(0)
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .min(0)
                                  .optional(),
                              })
                              .describe('Thumbnail details.')
                              .optional(),
                            video: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Video url.')
                                  .max(2000)
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Video width.')
                                  .min(0)
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Video height.')
                                  .min(0)
                                  .optional(),
                              })
                              .describe('Video details.')
                              .optional(),
                          })
                          .describe('Embed media details.'),
                      }),
                    ])
                  )
                  .describe('Post cover media.')
                  .optional(),
                previewTextParagraph: z
                  .number()
                  .int()
                  .describe(
                    'Number of paragraphs to display in a paid content preview for non-paying users.'
                  )
                  .min(0)
                  .optional()
                  .nullable(),
              })
              .describe('Draft post'),
            fieldMask: z.array(z.string()).optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      action: z
        .enum([
          'UPDATE',
          'UPDATE_PUBLISH',
          'UPDATE_SCHEDULE',
          'UPDATE_REVERT_TO_DRAFT',
          'UPDATE_CANCEL_SCHEDULE',
          'UPDATE_REJECT',
          'UPDATE_PUBLICATION',
        ])
        .optional(),
      scheduledPublishDate: z
        .date()
        .describe(
          "Posts' scheduled publish date when `action` is set to `UPDATE_SCHEDULE`."
        )
        .optional()
        .nullable(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the full updated draft post entities in the response.'
        )
        .optional(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for updating multiple draft posts.')
    .optional(),
});
export const BulkUpdateDraftPostsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk actions metadata for draft post.')
          .optional(),
        item: z
          .object({
            _id: z.string().describe('Draft post ID.').max(38).optional(),
            title: z.string().describe('Draft post title.').max(200).optional(),
            excerpt: z
              .string()
              .describe(
                "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
              )
              .max(500)
              .optional()
              .nullable(),
            featured: z
              .boolean()
              .describe('Whether the draft post is marked as featured.')
              .optional()
              .nullable(),
            categoryIds: z.array(z.string()).max(10).optional(),
            memberId: z
              .string()
              .describe("Draft post owner's member ID.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            hashtags: z.array(z.string()).max(100).optional(),
            commentingEnabled: z
              .boolean()
              .describe('Whether commenting on the draft post is enabled.')
              .optional()
              .nullable(),
            minutesToRead: z
              .number()
              .int()
              .describe(
                'Estimated reading time of the draft post (calculated automatically).'
              )
              .optional(),
            heroImage: z
              .string()
              .describe('Image placed at the top of the blog page.')
              .optional(),
            tagIds: z.array(z.string()).max(30).optional(),
            relatedPostIds: z.array(z.string()).max(3).optional(),
            pricingPlanIds: z.array(z.string()).max(100).optional(),
            translationId: z
              .string()
              .describe(
                "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            language: z
              .string()
              .describe(
                'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
              )
              .optional()
              .nullable(),
            richContent: z
              .any()
              .describe(
                'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
              )
              .optional(),
            status: z
              .enum([
                'UNKNOWN',
                'PUBLISHED',
                'UNPUBLISHED',
                'SCHEDULED',
                'DELETED',
                'IN_REVIEW',
              ])
              .describe('Status of the draft post.')
              .optional(),
            moderationDetails: z
              .object({
                submittedBy: z
                  .string()
                  .describe(
                    'Member ID of the person submitting the draft post for review.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                submittedDate: z
                  .date()
                  .describe('Date the post was submitted for review.')
                  .optional()
                  .nullable(),
                status: z
                  .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
                  .describe(
                    'Status indicating whether the submission was approved or rejected by the moderator.'
                  )
                  .optional(),
                moderatedBy: z
                  .string()
                  .describe(
                    'Member ID of the person who approved or rejected the post.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                moderationDate: z
                  .date()
                  .describe('Date the post was approved or rejected.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
              )
              .optional(),
            hasUnpublishedChanges: z
              .boolean()
              .describe(
                'Indicates if there are changes made to the draft post that have not yet been published.'
              )
              .optional(),
            editedDate: z
              .date()
              .describe('Date the draft post was last edited.')
              .optional()
              .nullable(),
            scheduledPublishDate: z
              .date()
              .describe('Date the draft post is scheduled to be published.')
              .optional()
              .nullable(),
            firstPublishedDate: z
              .date()
              .describe('Date the post was first published.')
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('SEO data.')
              .optional(),
            url: z
              .string()
              .describe(
                'Draft post URL preview. What the URL will look like once the post is published.'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date the draft post was first created.')
              .optional()
              .nullable(),
            seoSlug: z
              .string()
              .describe('SEO slug.')
              .max(100)
              .optional()
              .nullable(),
            media: z
              .intersection(
                z.object({
                  displayed: z
                    .boolean()
                    .describe('Whether cover media is displayed.')
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Media alternative text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z.never().optional(),
                  }),
                  z.object({
                    embedMedia: z.never().optional(),
                    wixMedia: z
                      .object({
                        image: z.string().describe('Image details.').optional(),
                        videoV2: z
                          .string()
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Wix Media details.'),
                  }),
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z
                      .object({
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Thumbnail details.')
                          .optional(),
                        video: z
                          .object({
                            url: z
                              .string()
                              .describe('Video url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Video width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Video height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Embed media details.'),
                  }),
                ])
              )
              .describe('Post cover media.')
              .optional(),
            previewTextParagraph: z
              .number()
              .int()
              .describe(
                'Number of paragraphs to display in a paid content preview for non-paying users.'
              )
              .min(0)
              .optional()
              .nullable(),
          })
          .describe('Optional full draft post.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const ListDeletedDraftPostsRequest = z.object({
  options: z
    .object({
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nIf omitted, deleted draft posts in all languages are returned.'
        )
        .optional()
        .nullable(),
      sort: z.enum(['EDITING_DATE_DESC', 'EDITING_DATE_ASC']).optional(),
      paging: z
        .object({
          offset: z
            .number()
            .int()
            .describe(
              'Number of items to skip in the current sort order.\n\n\nDefault: `0`'
            )
            .min(0)
            .optional(),
          limit: z
            .number()
            .int()
            .describe('Number of items to return.\n\n\nDefault:`50`')
            .min(1)
            .max(100)
            .optional(),
          cursor: z
            .string()
            .describe(
              'Pointer to the next or previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
      draftPostIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Options for listing deleted draft posts.')
    .optional(),
});
export const ListDeletedDraftPostsResponse = z.object({
  draftPosts: z
    .array(
      z.object({
        _id: z.string().describe('Draft post ID.').max(38).optional(),
        title: z.string().describe('Draft post title.').max(200).optional(),
        excerpt: z
          .string()
          .describe(
            "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
          )
          .max(500)
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe('Whether the draft post is marked as featured.')
          .optional()
          .nullable(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Draft post owner's member ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the draft post is enabled.')
          .optional()
          .nullable(),
        minutesToRead: z
          .number()
          .int()
          .describe(
            'Estimated reading time of the draft post (calculated automatically).'
          )
          .optional(),
        heroImage: z
          .string()
          .describe('Image placed at the top of the blog page.')
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PUBLISHED',
            'UNPUBLISHED',
            'SCHEDULED',
            'DELETED',
            'IN_REVIEW',
          ])
          .describe('Status of the draft post.')
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z
              .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
              .describe(
                'Status indicating whether the submission was approved or rejected by the moderator.'
              )
              .optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
          )
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there are changes made to the draft post that have not yet been published.'
          )
          .optional(),
        editedDate: z
          .date()
          .describe('Date the draft post was last edited.')
          .optional()
          .nullable(),
        scheduledPublishDate: z
          .date()
          .describe('Date the draft post is scheduled to be published.')
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        url: z
          .string()
          .describe(
            'Draft post URL preview. What the URL will look like once the post is published.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the draft post was first created.')
          .optional()
          .nullable(),
        seoSlug: z
          .string()
          .describe('SEO slug.')
          .max(100)
          .optional()
          .nullable(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        previewTextParagraph: z
          .number()
          .int()
          .describe(
            'Number of paragraphs to display in a paid content preview for non-paying users.'
          )
          .min(0)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(2000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const GetDraftPostRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
  options: z
    .object({
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for getting a draft post.')
    .optional(),
});
export const GetDraftPostResponse = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .describe('Status of the draft post.')
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Draft post info.')
    .optional(),
});
export const UpdateDraftPostRequest = z.object({
  _id: z.string().describe('Draft post ID.').max(38),
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z.enum(['UNKNOWN', 'APPROVED', 'REJECTED']).optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Draft Post info.'),
  options: z
    .object({
      action: z
        .enum([
          'UPDATE',
          'UPDATE_PUBLISH',
          'UPDATE_SCHEDULE',
          'UPDATE_REVERT_TO_DRAFT',
          'UPDATE_CANCEL_SCHEDULE',
          'UPDATE_REJECT',
          'UPDATE_PUBLICATION',
        ])
        .optional(),
      scheduledPublishDate: z
        .date()
        .describe(
          'Post publish schedule date if `action` is set to `UPDATE_SCHEDULE`.'
        )
        .optional()
        .nullable(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for updating a draft post.')
    .optional(),
});
export const UpdateDraftPostResponse = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .describe('Status of the draft post.')
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Updated draft post info.')
    .optional(),
});
export const DeleteDraftPostRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
  options: z
    .object({
      permanent: z
        .boolean()
        .describe(
          'Whether to bypass the trash bin and delete the post permanently.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe('Options for deleting a draft post.')
    .optional(),
});
export const DeleteDraftPostResponse = z.object({});
export const RemoveFromTrashBinRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
});
export const RemoveFromTrashBinResponse = z.object({});
export const BulkDeleteDraftPostsRequest = z.object({
  postIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      permanent: z
        .boolean()
        .describe('Should delete bypassing the trash-bin.')
        .optional(),
    })
    .describe('Options for deleting multiple draft posts.')
    .optional(),
});
export const BulkDeleteDraftPostsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk actions metadata for draft post.')
          .optional(),
        item: z
          .object({
            _id: z.string().describe('Draft post ID.').max(38).optional(),
            title: z.string().describe('Draft post title.').max(200).optional(),
            excerpt: z
              .string()
              .describe(
                "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
              )
              .max(500)
              .optional()
              .nullable(),
            featured: z
              .boolean()
              .describe('Whether the draft post is marked as featured.')
              .optional()
              .nullable(),
            categoryIds: z.array(z.string()).max(10).optional(),
            memberId: z
              .string()
              .describe("Draft post owner's member ID.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            hashtags: z.array(z.string()).max(100).optional(),
            commentingEnabled: z
              .boolean()
              .describe('Whether commenting on the draft post is enabled.')
              .optional()
              .nullable(),
            minutesToRead: z
              .number()
              .int()
              .describe(
                'Estimated reading time of the draft post (calculated automatically).'
              )
              .optional(),
            heroImage: z
              .string()
              .describe('Image placed at the top of the blog page.')
              .optional(),
            tagIds: z.array(z.string()).max(30).optional(),
            relatedPostIds: z.array(z.string()).max(3).optional(),
            pricingPlanIds: z.array(z.string()).max(100).optional(),
            translationId: z
              .string()
              .describe(
                "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            language: z
              .string()
              .describe(
                'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
              )
              .optional()
              .nullable(),
            richContent: z
              .any()
              .describe(
                'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
              )
              .optional(),
            status: z
              .enum([
                'UNKNOWN',
                'PUBLISHED',
                'UNPUBLISHED',
                'SCHEDULED',
                'DELETED',
                'IN_REVIEW',
              ])
              .describe('Status of the draft post.')
              .optional(),
            moderationDetails: z
              .object({
                submittedBy: z
                  .string()
                  .describe(
                    'Member ID of the person submitting the draft post for review.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                submittedDate: z
                  .date()
                  .describe('Date the post was submitted for review.')
                  .optional()
                  .nullable(),
                status: z
                  .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
                  .describe(
                    'Status indicating whether the submission was approved or rejected by the moderator.'
                  )
                  .optional(),
                moderatedBy: z
                  .string()
                  .describe(
                    'Member ID of the person who approved or rejected the post.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                moderationDate: z
                  .date()
                  .describe('Date the post was approved or rejected.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
              )
              .optional(),
            hasUnpublishedChanges: z
              .boolean()
              .describe(
                'Indicates if there are changes made to the draft post that have not yet been published.'
              )
              .optional(),
            editedDate: z
              .date()
              .describe('Date the draft post was last edited.')
              .optional()
              .nullable(),
            scheduledPublishDate: z
              .date()
              .describe('Date the draft post is scheduled to be published.')
              .optional()
              .nullable(),
            firstPublishedDate: z
              .date()
              .describe('Date the post was first published.')
              .optional()
              .nullable(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('SEO data.')
              .optional(),
            url: z
              .string()
              .describe(
                'Draft post URL preview. What the URL will look like once the post is published.'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date the draft post was first created.')
              .optional()
              .nullable(),
            seoSlug: z
              .string()
              .describe('SEO slug.')
              .max(100)
              .optional()
              .nullable(),
            media: z
              .intersection(
                z.object({
                  displayed: z
                    .boolean()
                    .describe('Whether cover media is displayed.')
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Media alternative text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z.never().optional(),
                  }),
                  z.object({
                    embedMedia: z.never().optional(),
                    wixMedia: z
                      .object({
                        image: z.string().describe('Image details.').optional(),
                        videoV2: z
                          .string()
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Wix Media details.'),
                  }),
                  z.object({
                    wixMedia: z.never().optional(),
                    embedMedia: z
                      .object({
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Thumbnail details.')
                          .optional(),
                        video: z
                          .object({
                            url: z
                              .string()
                              .describe('Video url.')
                              .max(2000)
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Video width.')
                              .min(0)
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Video height.')
                              .min(0)
                              .optional(),
                          })
                          .describe('Video details.')
                          .optional(),
                      })
                      .describe('Embed media details.'),
                  }),
                ])
              )
              .describe('Post cover media.')
              .optional(),
            previewTextParagraph: z
              .number()
              .int()
              .describe(
                'Number of paragraphs to display in a paid content preview for non-paying users.'
              )
              .min(0)
              .optional()
              .nullable(),
          })
          .describe('Optional full draft post.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const ListDraftPostsRequest = z.object({
  options: z
    .object({
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .optional(),
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nIf omitted, draft posts in all languages are returned.'
        )
        .optional()
        .nullable(),
      sort: z.enum(['EDITING_DATE_DESC', 'EDITING_DATE_ASC']).optional(),
      paging: z
        .object({
          offset: z
            .number()
            .int()
            .describe(
              'Number of items to skip in the current sort order.\n\n\nDefault: `0`'
            )
            .min(0)
            .optional(),
          limit: z
            .number()
            .int()
            .describe('Number of items to return.\n\n\nDefault:`50`')
            .min(1)
            .max(100)
            .optional(),
          cursor: z
            .string()
            .describe(
              'Pointer to the next or previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for listing multiple draft posts.')
    .optional(),
});
export const ListDraftPostsResponse = z.object({
  draftPosts: z
    .array(
      z.object({
        _id: z.string().describe('Draft post ID.').max(38).optional(),
        title: z.string().describe('Draft post title.').max(200).optional(),
        excerpt: z
          .string()
          .describe(
            "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
          )
          .max(500)
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe('Whether the draft post is marked as featured.')
          .optional()
          .nullable(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Draft post owner's member ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the draft post is enabled.')
          .optional()
          .nullable(),
        minutesToRead: z
          .number()
          .int()
          .describe(
            'Estimated reading time of the draft post (calculated automatically).'
          )
          .optional(),
        heroImage: z
          .string()
          .describe('Image placed at the top of the blog page.')
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PUBLISHED',
            'UNPUBLISHED',
            'SCHEDULED',
            'DELETED',
            'IN_REVIEW',
          ])
          .describe('Status of the draft post.')
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z
              .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
              .describe(
                'Status indicating whether the submission was approved or rejected by the moderator.'
              )
              .optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
          )
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there are changes made to the draft post that have not yet been published.'
          )
          .optional(),
        editedDate: z
          .date()
          .describe('Date the draft post was last edited.')
          .optional()
          .nullable(),
        scheduledPublishDate: z
          .date()
          .describe('Date the draft post is scheduled to be published.')
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        url: z
          .string()
          .describe(
            'Draft post URL preview. What the URL will look like once the post is published.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the draft post was first created.')
          .optional()
          .nullable(),
        seoSlug: z
          .string()
          .describe('SEO slug.')
          .max(100)
          .optional()
          .nullable(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        previewTextParagraph: z
          .number()
          .int()
          .describe(
            'Number of paragraphs to display in a paid content preview for non-paying users.'
          )
          .min(0)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metaData: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in this response.')
        .optional(),
      offset: z.number().int().describe('Requested offset.').optional(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional(),
      cursor: z
        .string()
        .describe(
          'Pointer to the next or previous page in the list of results.'
        )
        .max(2000)
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const GetDeletedDraftPostRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
});
export const GetDeletedDraftPostResponse = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .describe('Status of the draft post.')
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Draft post info.')
    .optional(),
});
export const RestoreFromTrashBinRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
});
export const RestoreFromTrashBinResponse = z.object({
  draftPost: z
    .object({
      _id: z.string().describe('Draft post ID.').max(38).optional(),
      title: z.string().describe('Draft post title.').max(200).optional(),
      excerpt: z
        .string()
        .describe(
          "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
        )
        .max(500)
        .optional()
        .nullable(),
      featured: z
        .boolean()
        .describe('Whether the draft post is marked as featured.')
        .optional()
        .nullable(),
      categoryIds: z.array(z.string()).max(10).optional(),
      memberId: z
        .string()
        .describe("Draft post owner's member ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      hashtags: z.array(z.string()).max(100).optional(),
      commentingEnabled: z
        .boolean()
        .describe('Whether commenting on the draft post is enabled.')
        .optional()
        .nullable(),
      minutesToRead: z
        .number()
        .int()
        .describe(
          'Estimated reading time of the draft post (calculated automatically).'
        )
        .optional(),
      heroImage: z
        .string()
        .describe('Image placed at the top of the blog page.')
        .optional(),
      tagIds: z.array(z.string()).max(30).optional(),
      relatedPostIds: z.array(z.string()).max(3).optional(),
      pricingPlanIds: z.array(z.string()).max(100).optional(),
      translationId: z
        .string()
        .describe(
          "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      richContent: z
        .any()
        .describe(
          'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
        )
        .optional(),
      status: z
        .enum([
          'UNKNOWN',
          'PUBLISHED',
          'UNPUBLISHED',
          'SCHEDULED',
          'DELETED',
          'IN_REVIEW',
        ])
        .describe('Status of the draft post.')
        .optional(),
      moderationDetails: z
        .object({
          submittedBy: z
            .string()
            .describe(
              'Member ID of the person submitting the draft post for review.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          submittedDate: z
            .date()
            .describe('Date the post was submitted for review.')
            .optional()
            .nullable(),
          status: z
            .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
            .describe(
              'Status indicating whether the submission was approved or rejected by the moderator.'
            )
            .optional(),
          moderatedBy: z
            .string()
            .describe(
              'Member ID of the person who approved or rejected the post.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          moderationDate: z
            .date()
            .describe('Date the post was approved or rejected.')
            .optional()
            .nullable(),
        })
        .describe(
          'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
        )
        .optional(),
      hasUnpublishedChanges: z
        .boolean()
        .describe(
          'Indicates if there are changes made to the draft post that have not yet been published.'
        )
        .optional(),
      editedDate: z
        .date()
        .describe('Date the draft post was last edited.')
        .optional()
        .nullable(),
      scheduledPublishDate: z
        .date()
        .describe('Date the draft post is scheduled to be published.')
        .optional()
        .nullable(),
      firstPublishedDate: z
        .date()
        .describe('Date the post was first published.')
        .optional()
        .nullable(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      url: z
        .string()
        .describe(
          'Draft post URL preview. What the URL will look like once the post is published.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date the draft post was first created.')
        .optional()
        .nullable(),
      seoSlug: z.string().describe('SEO slug.').max(100).optional().nullable(),
      media: z
        .intersection(
          z.object({
            displayed: z
              .boolean()
              .describe('Whether cover media is displayed.')
              .optional(),
            custom: z
              .boolean()
              .describe(
                "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
              )
              .optional(),
            altText: z
              .string()
              .describe('Media alternative text.')
              .min(1)
              .max(1000)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z.never().optional(),
            }),
            z.object({
              embedMedia: z.never().optional(),
              wixMedia: z
                .object({
                  image: z.string().describe('Image details.').optional(),
                  videoV2: z.string().describe('Video details.').optional(),
                })
                .describe('Wix Media details.'),
            }),
            z.object({
              wixMedia: z.never().optional(),
              embedMedia: z
                .object({
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Thumbnail details.')
                    .optional(),
                  video: z
                    .object({
                      url: z
                        .string()
                        .describe('Video url.')
                        .max(2000)
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Video width.')
                        .min(0)
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Video height.')
                        .min(0)
                        .optional(),
                    })
                    .describe('Video details.')
                    .optional(),
                })
                .describe('Embed media details.'),
            }),
          ])
        )
        .describe('Post cover media.')
        .optional(),
      previewTextParagraph: z
        .number()
        .int()
        .describe(
          'Number of paragraphs to display in a paid content preview for non-paying users.'
        )
        .min(0)
        .optional()
        .nullable(),
    })
    .describe('Restored draft post info.')
    .optional(),
});
export const QueryDraftPostsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          title: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          excerpt: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          scheduledPublishDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          editedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          categoryIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          hashtags: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          tagIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          pricingPlanIds: z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          memberId: z.object({}).partial().strict().optional(),
          featured: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          commentingEnabled: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          hasUnpublishedChanges: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          minutesToRead: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          language: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          translationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'title',
                'excerpt',
                'status',
                'scheduledPublishDate',
                'editedDate',
                'featured',
                'commentingEnabled',
                'hasUnpublishedChanges',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      fieldsets: z
        .array(
          z.enum([
            'UNKNOWN',
            'URL',
            'CONTENT',
            'RICH_CONTENT',
            'GENERATED_EXCERPT',
          ])
        )
        .max(10)
        .optional(),
    })
    .describe('Options for querying draft posts.')
    .optional(),
});
export const QueryDraftPostsResponse = z.object({
  draftPosts: z
    .array(
      z.object({
        _id: z.string().describe('Draft post ID.').max(38).optional(),
        title: z.string().describe('Draft post title.').max(200).optional(),
        excerpt: z
          .string()
          .describe(
            "Draft post excerpt.\n\nIf no excerpt has been manually set, an excerpt is automatically generated from the post's text.\nThis can be retrieved using the `GENERATED_EXCERPT` fieldset."
          )
          .max(500)
          .optional()
          .nullable(),
        featured: z
          .boolean()
          .describe('Whether the draft post is marked as featured.')
          .optional()
          .nullable(),
        categoryIds: z.array(z.string()).max(10).optional(),
        memberId: z
          .string()
          .describe("Draft post owner's member ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        hashtags: z.array(z.string()).max(100).optional(),
        commentingEnabled: z
          .boolean()
          .describe('Whether commenting on the draft post is enabled.')
          .optional()
          .nullable(),
        minutesToRead: z
          .number()
          .int()
          .describe(
            'Estimated reading time of the draft post (calculated automatically).'
          )
          .optional(),
        heroImage: z
          .string()
          .describe('Image placed at the top of the blog page.')
          .optional(),
        tagIds: z.array(z.string()).max(30).optional(),
        relatedPostIds: z.array(z.string()).max(3).optional(),
        pricingPlanIds: z.array(z.string()).max(100).optional(),
        translationId: z
          .string()
          .describe(
            "ID of the draft post's translations.\n\nAll translations of a single post share the same `translationId`.\nAvailable only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Language the draft post is written in.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        richContent: z
          .any()
          .describe(
            'Draft Post rich content.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PUBLISHED',
            'UNPUBLISHED',
            'SCHEDULED',
            'DELETED',
            'IN_REVIEW',
          ])
          .describe('Status of the draft post.')
          .optional(),
        moderationDetails: z
          .object({
            submittedBy: z
              .string()
              .describe(
                'Member ID of the person submitting the draft post for review.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            submittedDate: z
              .date()
              .describe('Date the post was submitted for review.')
              .optional()
              .nullable(),
            status: z
              .enum(['UNKNOWN', 'APPROVED', 'REJECTED'])
              .describe(
                'Status indicating whether the submission was approved or rejected by the moderator.'
              )
              .optional(),
            moderatedBy: z
              .string()
              .describe(
                'Member ID of the person who approved or rejected the post.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            moderationDate: z
              .date()
              .describe('Date the post was approved or rejected.')
              .optional()
              .nullable(),
          })
          .describe(
            'Details of the draft post in review. Only relevant to posts submitted by guest writers.'
          )
          .optional(),
        hasUnpublishedChanges: z
          .boolean()
          .describe(
            'Indicates if there are changes made to the draft post that have not yet been published.'
          )
          .optional(),
        editedDate: z
          .date()
          .describe('Date the draft post was last edited.')
          .optional()
          .nullable(),
        scheduledPublishDate: z
          .date()
          .describe('Date the draft post is scheduled to be published.')
          .optional()
          .nullable(),
        firstPublishedDate: z
          .date()
          .describe('Date the post was first published.')
          .optional()
          .nullable(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        url: z
          .string()
          .describe(
            'Draft post URL preview. What the URL will look like once the post is published.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date the draft post was first created.')
          .optional()
          .nullable(),
        seoSlug: z
          .string()
          .describe('SEO slug.')
          .max(100)
          .optional()
          .nullable(),
        media: z
          .intersection(
            z.object({
              displayed: z
                .boolean()
                .describe('Whether cover media is displayed.')
                .optional(),
              custom: z
                .boolean()
                .describe(
                  "Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media."
                )
                .optional(),
              altText: z
                .string()
                .describe('Media alternative text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z.never().optional(),
              }),
              z.object({
                embedMedia: z.never().optional(),
                wixMedia: z
                  .object({
                    image: z.string().describe('Image details.').optional(),
                    videoV2: z.string().describe('Video details.').optional(),
                  })
                  .describe('Wix Media details.'),
              }),
              z.object({
                wixMedia: z.never().optional(),
                embedMedia: z
                  .object({
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Thumbnail details.')
                      .optional(),
                    video: z
                      .object({
                        url: z
                          .string()
                          .describe('Video url.')
                          .max(2000)
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Video width.')
                          .min(0)
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Video height.')
                          .min(0)
                          .optional(),
                      })
                      .describe('Video details.')
                      .optional(),
                  })
                  .describe('Embed media details.'),
              }),
            ])
          )
          .describe('Post cover media.')
          .optional(),
        previewTextParagraph: z
          .number()
          .int()
          .describe(
            'Number of paragraphs to display in a paid content preview for non-paying users.'
          )
          .min(0)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(2000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const PublishDraftPostRequest = z.object({
  draftPostId: z.string().describe('Draft post ID.').max(38),
});
export const PublishDraftPostResponse = z.object({
  postId: z
    .string()
    .describe('Published post ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
